import type { FieldTypesOptions, PermissionValue } from "@appType/types";
import { resolveAuthUrl } from "@utils/resolve-auth-url/resolve-auth-url";
import type { TFunction } from "i18next";
import type { ToastContainerProps } from "react-toastify";
import { Bounce } from "react-toastify";

import type { ApplicationConfig } from "../schemas/config-schema";

export const USER_PARTNER_ID_KEY_SESSION_STORAGE = "partnerId";
export const USER_PARTNER_NAME_KEY_SESSION_STORAGE = "partnerName";
export const HANDED_OVER_PARTNER_KEY_SESSION_STORAGE = "handedOverPartner";
export const USER_APPLICATION_SESSION_STORAGE = "applicationData";
export const AUTH_HOOK_CONTEXT_ERROR =
	"Component must be wrapped with Auth hook context";
export const MODAL_HOOK_CONTEXT_ERROR =
	"Component must be wrapped with Modal hook context";

const runtimeEnv = typeof window !== "undefined" ? window._env_ : undefined;

const apiBaseUrl: string =
	runtimeEnv?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;

// `||`, not `??`: start.sh publishes every seeded key, so an unset one arrives
// as "" and must not mask the build-time value. Anything but an explicit
// "false" is dev — local runs never set it.
const isDev =
	(runtimeEnv?.VITE_IS_DEV || import.meta.env.VITE_IS_DEV) !== "false";

/**
 * Where the product switcher hands off to when `VITE_LUCID_URL` /
 * `VITE_CONSOLE_URL` are not set. Switching stays within the environment:
 * test hands off to test.
 */
const LUCID_URL = {
	dev: "https://test.lucid.zypl.ai/",
	prod: "https://lucid.zypl.ai/",
} as const;

const CONSOLE_URL = {
	dev: "https://console.dev.zypl.cloud/",
	prod: "https://console.zypl.cloud/",
} as const;

export const APPLICATION_CONFIG: ApplicationConfig = {
	url: apiBaseUrl,
	// The Auth Gateway is deployed separately from the scoring service and can
	// move to a host of its own, so it has its own variable; see resolveAuthUrl
	// for the fallback that keeps a single-URL deployment working.
	auth_url: resolveAuthUrl(
		[
			runtimeEnv?.VITE_AUTH_API_BASE_URL,
			import.meta.env.VITE_AUTH_API_BASE_URL,
		],
		apiBaseUrl,
	),
	// Build-time only: Vite bakes this into every asset URL in the shell, so
	// the router basepath has to be the value the bundle was built with.
	base_path: import.meta.env.VITE_BASE_PATH,
	show_ldap:
		(runtimeEnv?.VITE_SHOW_LDAP ?? import.meta.env.VITE_SHOW_LDAP) !== "false",
	is_dev: isDev,
	// Lucid reads the handoff code on any path before it renders, so its root
	// will do; it moves the user on to its own start page.
	lucid_url:
		runtimeEnv?.VITE_LUCID_URL ||
		import.meta.env.VITE_LUCID_URL ||
		(isDev ? LUCID_URL.dev : LUCID_URL.prod),
	// User and access administration across the zypl products. Test and
	// production each have their own deployment; it signs the user in on its own.
	console_url:
		runtimeEnv?.VITE_CONSOLE_URL ||
		import.meta.env.VITE_CONSOLE_URL ||
		(isDev ? CONSOLE_URL.dev : CONSOLE_URL.prod),
};

// Kept in localStorage under the key every zypl app uses, so a session
// carries over between them; dev and prod keep separate keys so a test
// session never passes for a production one.
export const USER_TOKEN_KEY_LOCAL_STORAGE = APPLICATION_CONFIG.is_dev
	? "zypl-test-token"
	: "zypl-token";
export const USER_REFRESH_TOKEN_KEY_LOCAL_STORAGE = APPLICATION_CONFIG.is_dev
	? "zypl-test-refresh-token"
	: "zypl-refresh-token";

/**
 * The Auth Gateway serves several products off one user base; the product a
 * login is scoped to is chosen by name, and omitting it silently authenticates
 * against the gateway's default product instead of this one.
 */
export const AUTH_PRODUCT = "zadmin";

/** The gateway's name for Lucid, the product a handoff code can be issued for. */
export const LUCID_PRODUCT = "Lucid";

/** The query parameter zypl apps pass a one-time sign-in code to each other in. */
export const HANDOFF_CODE_PARAM = "code";

/**
 * Sent alongside the code: the name of the organization the user was in, for
 * the next app to open if the user has it there too. Names, not ids — the apps
 * match partners by name.
 */
export const HANDOFF_PARTNER_PARAM = "partner";

/** Auth Gateway routes — relative to `APPLICATION_CONFIG.auth_url`. */
export const AUTH_API_ROUTES = {
	LOGIN: "/login",
	LOGIN_LDAP: "/login/ldap",
	LOGOUT: "/logout",
	REFRESH: "/refresh",
	WHOAMI: "/whoami",
	HANDOFF_CODE: "/handoff/code",
	HANDOFF_EXCHANGE: "/handoff/exchange",
	USERS: "/user/list",
	USER_RESET: (email: string) => `/user/${encodeURIComponent(email)}/reset`,
	// The login goes into the path as is — the gateway does not decode it here.
	CHANGE_TEMP_PASSWORD: (login: string) => `/user/${login}/change`,
} as const;

export const toastOptions: ToastContainerProps = {
	position: "top-right",
	autoClose: 5000,
	newestOnTop: true,
	closeOnClick: true,
	theme: "light",
	transition: Bounce,
};

export const supportedLanguages = ["en", "ru", "ar"];

export const API_ROUTES = {
	GET_MODELS: (partner_id: string) => `/partner/${String(partner_id)}/model/`,
	GET_MODELS_CONFIGURE: (mlmodel_id: string, partner_id: string) =>
		`/partner/${String(partner_id)}/model/${mlmodel_id}`,
	GET_MODEL_JSON: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/json`,
	CREATE_FEATURE_GROUP: (mlmodel_id: string, partner_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/feature/group`,
	GET_PARTNER_SCORING_LOG: (partner_id: string) =>
		`/partner/${partner_id}/scoring_logs`,
	DELETE_PARTNER_SCORING_LOG: (partner_id: string, scoring_id: number) =>
		`/partner/${partner_id}/scoring_log?scoring_id=${scoring_id}`,
	GET_PARTNER_SCORING_LOG_DETAIL: (partner_id: string, scoring_id: number) =>
		`/partner/${partner_id}/scoring_log?scoring_id=${scoring_id}`,
	ADD_FEATURE_TO_MODEL_GROUP: (mlmodel_id: string, partner_id: string) =>
		`/partner/${String(partner_id)}/model/${mlmodel_id}/feature/`,
	GET_PARTNER_ID: (partner_id: string) => `/partner/${partner_id}`,
	GET_PARTNER_ID_LOGO: (partner_id: string) => `/partner/${partner_id}/logo`,
	RENAME_MODEL_GROUP: (
		mlmodel_id: string,
		group_id: number,
		partner_id: string,
	) =>
		`/partner/${String(partner_id)}/model/${mlmodel_id}/feature/group/${group_id}`,
	DELETE_MODEL_GROUP: (
		mlmodel_id: string,
		group_id: number,
		partner_id: string,
	) =>
		`/partner/${String(partner_id)}/model/${mlmodel_id}/feature/group/${group_id}`,
	DELETE_MODEL_FEATURE: (
		mlmodel_id: string,
		feature_id: number,
		partner_id: string,
	) => `/partner/${partner_id}/model/${mlmodel_id}/feature/${feature_id}`,
	UPDATE_MODEL_FEATURE: (
		mlmodel_id: string,
		feature_id: string,
		partner_id: string,
	) => `/partner/${partner_id}/model/${mlmodel_id}/feature/${feature_id}`,
	MACRO_CONFIG: (mlmodel_id: string, feature_id: string, partner_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/feature/${feature_id}/macro_config`,
	RUN_SCORING: (mlmodel_id: string, partner_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score`,
	RUN_BULK_SCORING: (mlmodel_id: string, partner_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score_file`,
	RUN_GROUP_SCORING: (mlmodel_id: string, partner_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/group_score`,
	SUBMIT_FIRST_FLOW_APPLICATION: (mlmodel_id: string, partner_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/application`,
	GET_SECOND_FLOW_APPLICATION: (
		partner_id: string,
		mlmodel_id: string,
		first_step_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/application/${first_step_id}/second_step`,
	GET_GROUP_SCORING_RESULT: (
		partner_id: string,
		mlmodel_id: string,
		group_scoring_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/group_scoring/${group_scoring_id}`,
	GET_GROUP_SCORINGS: (partner_id: string) =>
		`/partner/${partner_id}/group_scorings`,
	UPDATE_MODEL: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}`,
	UPDATE_MODEL_THRESHOLD: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/threshold`,
	APPLICATION_XLSX: (partner_id: string) =>
		`/partner/${partner_id}/scoring_logs_xlsx`,
	APPLICATION_TEMPLATE_XLSX: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score_file`,
	GET_SHAP: (partner_id: string, mlmodel_id: string, scoring_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/shap?scoring_id=${scoring_id}`,
	GET_PDF: (partner_id: string) => `/partner/${partner_id}/scoring_log_pdf`,
	GET_GROUP_SCORING_PDF: (
		partner_id: string,
		mlmodel_id: string,
		group_scoring_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/group_scoring/${group_scoring_id}/pdf`,
	GET_MACRO: "/utils/macro",
	GET_PARTNERS: `/partner/list`,
	CREATE_PARTNER: `/partner/`,
	GET_GPT: (partner_id: string, mlmodel_id: string, scoring_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/gpt?scoring_id=${scoring_id}`,
	EDIT_SCORING_LOG: (
		partner_id: string,
		mlmodel_id: string,
		scoring_id: number,
	) => `/partner/${partner_id}/model/${mlmodel_id}/score/${scoring_id}`,
	EDIT_RIGHTS: (partner_id: string, mlmodel_id: string, scoring_id: number) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score/${scoring_id}/edit-rights`,
	GET_REC: (partner_id: string, mlmodel_id: string, scoring_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score/${scoring_id}/recommendations`,
	SAVE_REC: (partner_id: string, mlmodel_id: string, scoring_id: string) => `
		/partner/${partner_id}/model/${mlmodel_id}/score/${scoring_id}/save_recommendation
	`,
	SEND_SCORING_RESULTS: (
		partner_id: string,
		mlmodel_id: string,
		scoring_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score/${scoring_id}/send_scoring_results`,
	ADD_REC_SETTINGS: (
		partner_id: string,
		mlmodel_id: string,
		feature_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/feature/${feature_id}/recommendation_settings`,
	CREATE_THRESHOLD: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/thresholds/`,
	GET_THRESHOLDS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/thresholds/`,
	DELETE_ALL_THRESHOLDS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/thresholds/`,
	UPDATE_THRESHOLD: (
		partner_id: string,
		mlmodel_id: string,
		threshold_id: number,
	) => `/partner/${partner_id}/model/${mlmodel_id}/thresholds/${threshold_id}`,
	DELETE_THRESHOLD: (
		partner_id: string,
		mlmodel_id: string,
		threshold_id: number,
	) => `/partner/${partner_id}/model/${mlmodel_id}/thresholds/${threshold_id}`,
	CREATE_FEATURE_WEIGHT: (
		partner_id: string,
		mlmodel_id: string,
		threshold_id: number,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/thresholds/${threshold_id}/feature_weight`,
	GET_FEATURE_WEIGHTS: (
		partner_id: string,
		mlmodel_id: string,
		threshold_id: number,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/thresholds/${threshold_id}/feature_weights`,
	GET_FEATURE_WEIGHT: (
		partner_id: string,
		mlmodel_id: string,
		threshold_id: number,
		weight_id: number,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/thresholds/${threshold_id}/feature_weight/${weight_id}`,
	UPDATE_FEATURE_WEIGHT: (
		partner_id: string,
		mlmodel_id: string,
		threshold_id: number,
		weight_id: number,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/thresholds/${threshold_id}/feature_weight/${weight_id}`,
	DELETE_FEATURE_WEIGHT: (
		partner_id: string,
		mlmodel_id: string,
		threshold_id: number,
		weight_id: number,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/thresholds/${threshold_id}/feature_weight/${weight_id}`,
	UPDATE_REC_SETTINGS: (
		partner_id: string,
		mlmodel_id: string,
		feature_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/feature/${feature_id}/recommendation_settings`,
	DELETE_REC_SETTINGS: (
		partner_id: string,
		mlmodel_id: string,
		feature_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/feature/${feature_id}/recommendation_settings`,

	DELETE_VALIDATOR: (
		partner_id: string,
		mlmodel_id: string,
		feature_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/feature/${feature_id}/validator`,
	UPDATE_DISB_DATE: (partner_id: string) =>
		`/partner/${partner_id}/scoring_log`,
	// Scoring & model action log, kept by the scoring service.
	GET_PARTNER_ACTION_LOG: (partner_id: string) =>
		`/partner/${partner_id}/action_log`,
	// Model change requests. When the partner has `model_change_approval` on,
	// a feature/OCR edit is queued as a change request instead of taking
	// effect, and an approver decides on it here.
	//
	// The diff is its own endpoint rather than a field on the request: it is a
	// dry-run of the change against the *current* configuration, so it can go
	// stale while the request sits in the queue. It answers with the
	// `diff_hash` that `APPROVE_CHANGE_REQUEST` demands back, which is what
	// stops a reviewer approving a diff they were never shown.
	GET_CHANGE_REQUESTS: (partner_id: string) =>
		`/partner/${partner_id}/change_request/`,
	GET_CHANGE_REQUEST_DIFF: (partner_id: string, cr_id: string) =>
		`/partner/${partner_id}/change_request/${cr_id}/diff`,
	APPROVE_CHANGE_REQUEST: (partner_id: string, cr_id: string) =>
		`/partner/${partner_id}/change_request/${cr_id}/approve`,
	REJECT_CHANGE_REQUEST: (partner_id: string, cr_id: string) =>
		`/partner/${partner_id}/change_request/${cr_id}/reject`,
	WITHDRAW_CHANGE_REQUEST: (partner_id: string, cr_id: string) =>
		`/partner/${partner_id}/change_request/${cr_id}/withdraw`,
	STOP_FACTORS_DATA: () => `utils/stop_factors_data`,
	DELETE_STOP_FACTOR: (
		partner_id: string,
		mlmodel_id: string,
		stop_factor_id: number,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/stop_factor/${stop_factor_id}`,
	CREATE_STOP_FACTOR: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/stop_factor`,
	UPDATE_STOP_FACTOR: (
		partner_id: string,
		mlmodel_id: string,
		stop_factor_id: number,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/stop_factor/${stop_factor_id}`,

	CREATE_MARKER: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/marker`,
	UPDATE_MARKER: (partner_id: string, mlmodel_id: string, marker_id: number) =>
		`/partner/${partner_id}/model/${mlmodel_id}/marker/${marker_id}`,
	DELETE_MARKER: (partner_id: string, mlmodel_id: string, marker_id: number) =>
		`/partner/${partner_id}/model/${mlmodel_id}/marker/${marker_id}`,
	GET_MARKERS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/markers`,

	// Repeat-application limit rules (per-ID throttling). Scoped to one model —
	// the backend keys a rule by (partner, model, feature), so there is no
	// partner-wide rule and no query filter.
	GET_REPEAT_RULES: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/repeat_rule`,
	CREATE_REPEAT_RULE: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/repeat_rule`,
	UPDATE_REPEAT_RULE: (
		partner_id: string,
		mlmodel_id: string,
		rule_id: number,
	) => `/partner/${partner_id}/model/${mlmodel_id}/repeat_rule/${rule_id}`,
	DELETE_REPEAT_RULE: (
		partner_id: string,
		mlmodel_id: string,
		rule_id: number,
	) => `/partner/${partner_id}/model/${mlmodel_id}/repeat_rule/${rule_id}`,

	GET_OCR: (partner_id: string) => `/partner/${partner_id}/ocr/`,
	CREATE_OCR: (partner_id: string) => `/partner/${partner_id}/ocr/`,
	GET_OCR_BY_ID: (partner_id: string, ocrId: string) =>
		`/partner/${partner_id}/ocr/${ocrId}`,
	UPDATE_OCR: (partner_id: string, ocrId: string) =>
		`/partner/${partner_id}/ocr/${ocrId}`,
	DELETE_OCR: (partner_id: string, ocrId: string) =>
		`/partner/${partner_id}/ocr/${ocrId}`,
	CREATE_OCR_FILE: (partner_id: string, ocrId: string) =>
		`/partner/${partner_id}/ocr/${ocrId}/ocr_file`,
	UPDATE_OCR_FILE: (partner_id: string, ocrId: string, fileId: string) =>
		`/partner/${partner_id}/ocr/${ocrId}/ocr_file/${fileId}`,
	DELETE_OCR_FILE: (partner_id: string, ocrId: string, fileId: string) =>
		`/partner/${partner_id}/ocr/${ocrId}/ocr_file/${fileId}`,
	SEND_FILE_TO_OCR: (partner_id: string, ocrId: string) =>
		`/partner/${partner_id}/ocr/${ocrId}`,
	SEND_OCR_RESULT: (partner_id: string, ocrId: string) =>
		`/partner/${partner_id}/ocr/${ocrId}/result`,
	GET_OCR_CONFIG: (partner_id: string, ocr_id: string) =>
		`/partner/${partner_id}/ocr/${ocr_id}`,

	CREATE_OCR_STOP_FACTOR: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/stop_factor`,
	UPDATE_OCR_STOP_FACTOR: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
		stop_factor_id: number,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/stop_factor/${stop_factor_id}`,
	DELETE_OCR_STOP_FACTOR: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
		stop_factor_id: number,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/stop_factor/${stop_factor_id}`,

	UPDATE_OCR_FEATURE: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
		feature_id: string,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/feature/${feature_id}`,

	RENAME_OCR_GROUP: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
		group_id: number,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/feature/group/${group_id}`,
	DELETE_OCR_FEATURE: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
		feature_id: string,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/feature/${feature_id}`,
	DELETE_OCR_FEATURE_VALIDATOR: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
		feature_id: string,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/feature/${feature_id}/validator`,

	DELETE_OCR_FEATURE_GROUP: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
		group_id: number,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/feature/group/${group_id}`,
	CREATE_OCR_FEATURE_GROUP: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
	) =>
		`/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/feature/group`,

	CREATE_OCR_FEATURE: (
		partner_id: string,
		ocr_id: string,
		ocr_file_id: string,
	) => `/partner/${partner_id}/ocr/${ocr_id}/ocr_file/${ocr_file_id}/feature/`,

	OVERRIDE_SCORING_LOG_APPROVAL: (partner_id: string, scoring_id: string) =>
		`/partner/${partner_id}/scoring_log/${scoring_id}/approval`,

	CREATE_COMMENT: (partner_id: string, scoring_id: string) =>
		`/partner/${partner_id}/scoring_log/${scoring_id}/comment`,
	GET_COMMENTS: (partner_id: string, scoring_id: string) =>
		`/partner/${partner_id}/scoring_log/${scoring_id}/comments`,
	UPDATE_COMMENT: (
		partner_id: string,
		scoring_id: string,
		comment_id: string,
	) =>
		`/partner/${partner_id}/scoring_log/${scoring_id}/comments/${comment_id}`,
	DELETE_COMMENT: (
		partner_id: string,
		scoring_id: string,
		comment_id: string,
	) =>
		`/partner/${partner_id}/scoring_log/${scoring_id}/comments/${comment_id}`,

	GET_DASHBOARD_DATA: (partner_id: string) =>
		`/partner/${partner_id}/dashboard_data`,

	SCORE_STOP_FACTOR: (mlmodel_id: string, partner_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score_stop_factors`,

	GET_CLIENT_INN_INFO: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/get_client_inn_info`,

	GET_EXCEPTIONS: (
		partner_id: string,
		mlmodel_id: string,
		stop_factor_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/stop_factor/${stop_factor_id}/exceptions`,

	CREATE_EXCEPTION: (
		partner_id: string,
		mlmodel_id: string,
		stop_factor_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/stop_factor/${stop_factor_id}/exceptions`,

	UPDATE_EXCEPTION: (
		partner_id: string,
		mlmodel_id: string,
		stop_factor_id: string,
		exception_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/stop_factor/${stop_factor_id}/exceptions/${exception_id}`,

	DELETE_EXCEPTION: (
		partner_id: string,
		mlmodel_id: string,
		stop_factor_id: string,
		exception_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/stop_factor/${stop_factor_id}/exceptions/${exception_id}`,

	UPLOAD_SCORE_MAPPINGS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score-mapping/upload`,
	GET_SCORE_MAPPINGS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score-mapping/`,
	DELETE_ALL_SCORE_MAPPINGS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/score-mapping/`,

	// Risk Engine
	GET_RISK: (partner_id: string, mlmodel_id: string, scoring_id: number) =>
		`/partner/${partner_id}/model/${mlmodel_id}/ifrs?scoring_id=${scoring_id}`,
	IFRS_SOURCE_GRAPHS: (partner_id: string, source: "file" | "logs") =>
		`/partner/${partner_id}/ifrs/${source}/graphs`,
	IFRS_SOURCE_CONFIG: (partner_id: string, source: "file" | "logs") =>
		`/partner/${partner_id}/ifrs/${source}/config`,
	IFRS_FILE_COLUMNS: (partner_id: string) =>
		`/partner/${partner_id}/ifrs/file/columns`,
	IFRS_FILE_MIGRATION_MATRIX: (partner_id: string) =>
		`/partner/${partner_id}/ifrs/file/migration-matrix`,
	IFRS_MIGRATION_CONFIG: (partner_id: string, source: "file" | "logs") =>
		`/partner/${partner_id}/ifrs/migration-matrix/${source}/config`,

	// Portfolio dashboard — every page of the section in one root-only payload
	PORTFOLIO_DASHBOARD: (partner_id: string) =>
		`/partner/${partner_id}/portfolio/dashboard`,

	// Model monitoring (Evidently)
	MONITORING_STATUSES: (partner_id: string) =>
		`/partner/${partner_id}/monitoring/status`,
	MONITORING_GLOBAL_CONFIG: (partner_id: string) =>
		`/partner/${partner_id}/monitoring/config`,
	MONITORING_STATUS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/status`,
	MONITORING_TREND: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/trend`,
	MONITORING_CHECKS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/checks`,
	MONITORING_REPORT: (
		partner_id: string,
		mlmodel_id: string,
		check_id: string,
	) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/checks/${check_id}/report`,
	MONITORING_CHECK: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/check`,
	MONITORING_CONFIG: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/config`,
	MONITORING_RECIPIENTS: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/recipients`,
	MONITORING_REGISTER: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/register`,
	MONITORING_REGISTRATION: (partner_id: string, mlmodel_id: string) =>
		`/partner/${partner_id}/model/${mlmodel_id}/monitoring/registration`,

	// Partner Financials
	GET_FINANCIAL_DATA: (partner_id: string) =>
		`/partner/${partner_id}/financial_data/`,
	CREATE_FINANCIAL_DATA: (partner_id: string) =>
		`/partner/${partner_id}/financial_data/`,
	UPDATE_FINANCIAL_DATA: (partner_id: string) =>
		`/partner/${partner_id}/financial_data/`,
	DELETE_FINANCIAL_DATA: (partner_id: string) =>
		`/partner/${partner_id}/financial_data/`,

	// Async job polling
	RESTATE_TASK_STATUS: (partner_id: string, job_id: string) =>
		`/partner/${partner_id}/restate_tasks/${job_id}/status`,
	RESTATE_TASK_RESULT: (partner_id: string, job_id: string) =>
		`/partner/${partner_id}/restate_tasks/${job_id}/result`,
	RESTATE_TASK_WS: (partner_id: string, job_id: string) =>
		`/ws/partner/${partner_id}/restate_tasks/${job_id}/status`,
	// Streams the IFRS portfolio xlsx rendered from a finished job
	RESTATE_TASK_REPORT: (partner_id: string, job_id: string) =>
		`/partner/${partner_id}/restate_tasks/${job_id}/report`,
};

export const AION_API_ROUTES = {
	LOAN_REQUESTS: "/loan-requests",
	LOAN_REQUESTS_EXPORT: "/loan-requests/export",
	LOAN_REQUEST_BY_ID: (loanRequestId: number | string) =>
		`/loan-requests/${loanRequestId}`,
	LOAN_REQUEST_STATUS: (loanRequestId: number | string) =>
		`/loan-requests/${loanRequestId}/status`,
	SUBJECT_BY_INN: "/bureau/subject",
	CREATE_APP: "/loan-requests",
	CONTRACTS: "/contracts",
	CONTRACT_BY_ID: (contractId: number | string) => `/contracts/${contractId}`,
} as const;

export const ROLES = ["admin"] as const;
export const AUTH_SCHEME = ["PASSWORD"] as const;

export const fieldType = [
	"string",
	"integer",
	"number",
	"float",
	"boolean",
	"category",
	"synthetic",
	"date",
	"timedelta",
] as const;

export const featureTag = [
	"application",
	"scoring",
	"macro",
	"synthetic",
	"misc",
	"variable",
	"ocr",
] as const;

export const validatorType = [
	"boundary",
	"synthetic",
	"enum",
	"date",
	"timedelta",
] as const;

export const featureStepTag = [
	"first_step",
	"first_step_flexible",
	"second_step",
] as const;

export const permissionsValues = {
	seeAllLogs: "see_all_logs",
	seeShap: "see_shap",
	createAndScoreApplication: "create_and_score_application",
	getReport: "get_report",
	manageUsers: "manage_users",
	scoringSettings: "scoring_settings",
	modelSettings: "model_settings",
	ocrSettings: "ocr_settings",
	archiveAndRestoreApplications: "archive_and_restore_applications",
	deleteApplications: "delete_applications",
	audit: "audit",
	root: "root",
	performGroupScoring: "perform_group_scoring",
	scoreBulk: "score_bulk",
	readResults: "read_results",
	readDashboard: "read_dashboard",
	ifrsConfig: "ifrs_config",
	performIfrs: "perform_ifrs",
	editAndRescoreApplication: "edit_and_rescore_application",
	assignEditRights: "assign_edit_rights",
	editAssignedApplication: "edit_assigned_application",
	editAndRescoreOwnApplication: "edit_and_rescore_own_application",
	notionTicketing: "notion_ticketing",
	overrideApproval: "approval_override_history",
	seeThreshold: "see_threshold",
	approveModelChanges: "approve_model_changes",
} as const;

/**
 * Mirrors the gateway's `can_see_threshold`: the dedicated permission is not the
 * only way in — editing scoring or model settings implies it, and root sees
 * everything. Keep in sync with `utils/auth.py`.
 */
export const canSeeThreshold = (permissions: {
	root: boolean;
	see_threshold: boolean;
	scoring_settings: boolean;
	model_settings: boolean;
}): boolean =>
	permissions.root ||
	permissions.see_threshold ||
	permissions.scoring_settings ||
	permissions.model_settings;

export const permissions = (
	t: TFunction,
): { title: string; subtitle: string; value: PermissionValue }[] => [
	{
		title: t("permissions.view_all_applications_title"),
		subtitle: t("permissions.view_all_applications_subtitle"),
		value: permissionsValues.seeAllLogs,
	},
	{
		title: t("permissions.create_application_title"),
		subtitle: t("permissions.create_application_subtitle"),
		value: permissionsValues.createAndScoreApplication,
	},
	{
		title: t("permissions.download_applications_title"),
		subtitle: t("permissions.download_applications_subtitle"),
		value: permissionsValues.getReport,
	},
	{
		title: t("permissions.delete_applications_title"),
		subtitle: t("permissions.delete_applications_subtitle"),
		value: permissionsValues.deleteApplications,
	},
	{
		title: t("permissions.user_managment_title"),
		subtitle: t("permissions.user_managment_subtitle"),
		value: permissionsValues.manageUsers,
	},
	{
		title: t("permissions.scoring_settings_title"),
		subtitle: t("permissions.scoring_settings_subtitle"),
		value: permissionsValues.scoringSettings,
	},
	{
		title: t("permissions.model_settings_title"),
		subtitle: t("permissions.model_settings_subtitle"),
		value: permissionsValues.modelSettings,
	},
	{
		title: t("permissions.ocr_settings_title"),
		subtitle: t("permissions.ocr_settings_subtitle"),
		value: permissionsValues.ocrSettings,
	},
	{
		title: t("permissions.see_shap_title"),
		subtitle: t("permissions.see_shap_subtitle"),
		value: permissionsValues.seeShap,
	},
	{
		title: t("permissions.users_audit"),
		subtitle: t("permissions.users_audit_subtitle"),
		value: permissionsValues.audit,
	},
	{
		title: t("permissions.perform_group_scoring_title"),
		subtitle: t("permissions.perform_group_scoring_subtitle"),
		value: permissionsValues.performGroupScoring,
	},
	{
		title: t("permissions.bulk_title"),
		subtitle: t("permissions.bulk_subtitle"),
		value: permissionsValues.scoreBulk,
	},
	{
		title: t("permissions.read_title"),
		subtitle: t("permissions.read_subtitle"),
		value: permissionsValues.readResults,
	},
	{
		title: t("permissions.can_view_dashboard_title"),
		subtitle: t("permissions.can_view_dashboard_subtitle"),
		value: permissionsValues.readDashboard,
	},
	{
		title: t("permissions.ifrs_config_title"),
		subtitle: t("permissions.ifrs_config_subtitle"),
		value: permissionsValues.ifrsConfig,
	},
	{
		title: t("permissions.perform_ifrs_title"),
		subtitle: t("permissions.perform_ifrs_subtitle"),
		value: permissionsValues.performIfrs,
	},
	{
		title: t("permissions.edit_and_rescore_application_title"),
		subtitle: t("permissions.edit_and_rescore_application_subtitle"),
		value: permissionsValues.editAndRescoreApplication,
	},
	{
		title: t("permissions.assign_edit_rights_title"),
		subtitle: t("permissions.assign_edit_rights_subtitle"),
		value: permissionsValues.assignEditRights,
	},
	{
		title: t("permissions.edit_and_rescore_own_application_title"),
		subtitle: t("permissions.edit_and_rescore_own_application_subtitle"),
		value: permissionsValues.editAndRescoreOwnApplication,
	},
	{
		title: t("permissions.notion_ticketing_title"),
		subtitle: t("permissions.notion_ticketing_subtitle"),
		value: permissionsValues.notionTicketing,
	},
	{
		title: t("permissions.override_approval_title"),
		subtitle: t("permissions.override_approval_subtitle"),
		value: permissionsValues.overrideApproval,
	},
	{
		title: t("permissions.see_threshold_title"),
		subtitle: t("permissions.see_threshold_subtitle"),
		value: permissionsValues.seeThreshold,
	},
	{
		title: t("permissions.approve_model_changes_title"),
		subtitle: t("permissions.approve_model_changes_subtitle"),
		value: permissionsValues.approveModelChanges,
	},
];

export const statusApplication = (t: TFunction) => [
	{
		title: t("applications.status.draft"),
		value: "first_step_successful",
	},
	{
		title: t("applications.status.successful"),
		value: "successful",
	},
	{
		title: t("applications.status.rejected"),
		value: "rejected",
	},
];

export const fieldTypesOptions = (t: TFunction): FieldTypesOptions[] => [
	{
		label: t("settings.field_types.string"),
		value: "string",
	},
	{
		label: t("settings.field_types.int"),
		value: "integer",
	},
	{
		label: t("settings.field_types.float"),
		value: "float",
	},
	{
		label: t("settings.field_types.boolean"),
		value: "boolean",
	},
	{
		label: t("settings.field_types.category"),
		value: "category",
	},
	{
		label: t("settings.field_types.synthetic"),
		value: "synthetic",
	},
	{
		label: t("settings.field_types.date"),
		value: "date",
	},
	{
		label: t("settings.field_types.timedelta"),
		value: "timedelta",
	},
];

// Stop factors
export const expressionConstants = [
	"string",
	"integer",
	"float",
	"boolean",
] as const;

export const stopFactorStatuses = [
	"passed",
	"failed",
	"rejected",
	"review",
	"warning",
] as const;

export const DEFAULT_CLIENT_INFO = {
	Standard: [
		{
			name: "active_loans_count",
			description: "active_loans_count",
			is_table: false,
			user_input: null,
			label: null,
		},
		{
			name: "non_active_loans_count",
			description: "non_active_loans_count",
			is_table: false,
			user_input: null,
			label: null,
		},
		{
			name: "total_loan_amount_tjk",
			description: "total_loan_amount_tjk",
			is_table: false,
			user_input: null,
			label: null,
		},
	],
};
